import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import BackToTopButton from './BackToTopButton';
import FloatingContactStack from './FloatingContactStack';
import Header from './Header';
import AdSlider from './AdSlider';
import Footer from './Footer';
import NewsSection from './sections/NewsSection';
import GallerySection from './sections/GallerySection';
import ProjectsSection from './sections/ProjectsSection';
import ContactSection from './sections/ContactSection';
import PartnersSection from './sections/PartnersSection';
import SbhrpSection from './sections/SbhrpSection';
import VocationalVideoSection from './sections/VocationalVideoSection';
import useTranslation from '../utils/useTranslation';

function SkipLink() {
  const { t } = useTranslation();
  return (
    <a className="skip-link" href="#main">
      {t('skip_to_content')}
    </a>
  );
}

const SCROLL_TARGETS = ['contact', 'news-union', 'news-chambers'];

export default function HomePage() {
  const location = useLocation();
  const routerNavigate = useNavigate();
  const active = 'home';

  const refs = {
    news: useRef(null),
    gallery: useRef(null),
    projects: useRef(null),
    contact: useRef(null),
  };

  const navigate = (id) => {
    if (id === 'home') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    if (id === 'projects') {
      routerNavigate('/projects');
      return;
    }
    if (id === 'gallery') {
      routerNavigate('/gallery');
      return;
    }
    const el = refs[id]?.current || document.getElementById(id);
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.scrollY - 70;
    window.scrollTo({ top, behavior: 'smooth' });
  };

  // When arriving from another page: scroll to top (brand click) or to a section.
  useEffect(() => {
    if (location.state?.scrollToTop) {
      const id = setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 80);
      return () => clearTimeout(id);
    }
    const target = location.state?.scrollTo;
    if (!target) return undefined;
    if (target === 'projects') {
      routerNavigate('/projects');
      return undefined;
    }
    if (target === 'gallery') {
      routerNavigate('/gallery');
      return undefined;
    }
    if (!SCROLL_TARGETS.includes(target) && !refs[target]) return undefined;
    const id = setTimeout(() => navigate(target), 80);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  return (
    <div className="site">
      <SkipLink />
      <Header active={active} onNavigate={navigate} />
      <AdSlider />
      <PartnersSection />
      <SbhrpSection />
      <VocationalVideoSection />

      <main id="main">
        <ProjectsSection innerRef={refs.projects} />
        <NewsSection innerRef={refs.news} />
        <GallerySection innerRef={refs.gallery} />
        <ContactSection innerRef={refs.contact} />
      </main>

      <Footer />
      <FloatingContactStack />
      <BackToTopButton />
    </div>
  );
}
